export interface FollowupFormData {
  headacheLogged?: boolean;
  missingDiaryInfo?: string;
  headacheAssessment?:
    | 'much_better'
    | 'somewhat_better'
    | 'unchanged'
    | 'somewhat_worse'
    | 'much_worse';
  daysAssessment?:
    | 'much_fewer'
    | 'somewhat_fewer'
    | 'unchanged'
    | 'somewhat_more'
    | 'much_more';
  painIntensityChange?:
    | 'much_less'
    | 'somewhat_less'
    | 'unchanged'
    | 'somewhat_more'
    | 'much_more';
  attackDurationChange?:
    | 'much_shorter'
    | 'somewhat_shorter'
    | 'unchanged'
    | 'somewhat_longer'
    | 'much_longer';
  hasSideEffects?: boolean;
  sideEffectDescription?: string;
  suspectedDrug?: string;
  stoppedMedication?: boolean;
  stoppedMedicationName?: string;
  stoppedMedicationDate?: string;
  stoppedMedicationReason?: 'side_effects' | 'no_effect' | 'other';
  stoppedMedicationOtherReason?: string;
  hasStartedNewMedication?: boolean;
  newMedicationName?: string;
  newMedicationStartDate?: string;
  newMedicationReason?: string;
  hasNewCondition?: boolean;
  newConditionDescription?: string;
  hasOtherImportantInfo?: boolean;
  otherImportantInfo?: string;
  bloodPressureInstruction?: 'yes_measured' | 'yes_not_measured' | 'no';
  systolicPressure?: string;
  diastolicPressure?: string;
  pulse?: string;
  needsPrescriptionRenewal?: boolean;
  prescriptionDetails?: string;
  midasWorkDays?: number;
  midasReducedWorkDays?: number;
  midasHouseholdMissed?: number;
  midasHouseholdReduced?: number;
  midasSocialMissed?: number;
  midasPainSeverity?: number;
  estimatedHeadacheDaysLast3Months?: string;
  hitPainIntensity?: number;
  hitActivityLimitation?: number;
  hitDesireToLieDown?: number;
  hitFatigueFromHeadache?: number;
  hitIrritabilityFromHeadache?: number;
  hitConcentrationLimitation?: number;
}

export function formatDiarySection(data: FollowupFormData): string | null {
  const { headacheLogged, missingDiaryInfo } = data;
  if (headacheLogged === true)
    return 'Patienten har registrerat all huvudvärk i dagboken';
  if (headacheLogged === false)
    return `Patienten har inte registrerat all huvudvärk i dagboken. Patienten tillägger: ${missingDiaryInfo?.trim() || '[saknas]'}`;
  return null;
}

export function formatHeadacheSummarySection(
  data: FollowupFormData
): string | null {
  const assessment = formatHeadacheAssessment(data.headacheAssessment);
  const days = formatDaysAssessment(data.daysAssessment);
  const result = [assessment, days].filter(Boolean).join(' ');
  return result || null;
}

function formatHeadacheAssessment(
  value?: FollowupFormData['headacheAssessment']
): string {
  switch (value) {
    case 'much_better':
      return 'Upplever att huvudvärken är betydligt bättre sedan sist';
    case 'somewhat_better':
      return 'Upplever att huvudvärken är något bättre sedan sist';
    case 'unchanged':
      return 'Upplever att huvudvärken är oförändrad sedan sist';
    case 'somewhat_worse':
      return 'Upplever att huvudvärken är något sämre sedan sist';
    case 'much_worse':
      return 'Upplever att huvudvärken är betydligt sämre sedan sist';
    default:
      return '';
  }
}

function formatDaysAssessment(
  value?: FollowupFormData['daysAssessment']
): string {
  switch (value) {
    case 'much_fewer':
      return 'med subjektivt betydligt färre antal huvudvärksdagar.';
    case 'somewhat_fewer':
      return 'med subjektivt något färre antal huvudvärksdagar.';
    case 'unchanged':
      return 'med subjektivt oförändrat antal huvudvärksdagar.';
    case 'somewhat_more':
      return 'med subjektivt något fler antal huvudvärksdagar.';
    case 'much_more':
      return 'med subjektivt betydligt fler antal huvudvärksdagar.';
    default:
      return '';
  }
}

export function formatPainIntensitySection(
  data: FollowupFormData
): string | null {
  switch (data.painIntensityChange) {
    case 'much_less':
      return 'Smärtintensiteten vid anfallen bedöms betydligt lindrigare.';
    case 'somewhat_less':
      return 'Smärtintensiteten vid anfallen bedöms något lindrigare.';
    case 'unchanged':
      return 'Smärtintensiteten vid anfallen bedöms oförändrad.';
    case 'somewhat_more':
      return 'Smärtintensiteten vid anfallen bedöms något kraftigare.';
    case 'much_more':
      return 'Smärtintensiteten vid anfallen bedöms betydligt kraftigare.';
    default:
      return null;
  }
}

export function formatAttackDurationSection(
  data: FollowupFormData
): string | null {
  switch (data.attackDurationChange) {
    case 'much_shorter':
      return 'Anfallsdurationen upplevs betydligt kortare.';
    case 'somewhat_shorter':
      return 'Anfallsdurationen upplevs något kortare.';
    case 'unchanged':
      return 'Anfallsdurationen upplevs oförändrad.';
    case 'somewhat_longer':
      return 'Anfallsdurationen upplevs något längre.';
    case 'much_longer':
      return 'Anfallsdurationen upplevs betydligt längre.';
    default:
      return null;
  }
}

export function formatSideEffectSection(data: FollowupFormData): string | null {
  if (data.hasSideEffects === true)
    return `Upplever biverkan i form av ${data.sideEffectDescription?.trim() || '[saknas]'}. Misstänkt preparat: ${data.suspectedDrug?.trim() || '[saknas]'}.`;
  if (data.hasSideEffects === false) return 'Upplever ingen biverkan.';
  return null;
}

export function formatStoppedMedicationSection(
  data: FollowupFormData
): string | null {
  if (data.stoppedMedication === false)
    return 'Patienten har inte slutat med någon medicin.';
  if (data.stoppedMedication === true) {
    const name = data.stoppedMedicationName?.trim() || '[saknas]';
    const date = data.stoppedMedicationDate?.trim() || '[saknas]';
    let reason = '[saknas]';
    switch (data.stoppedMedicationReason) {
      case 'side_effects':
        reason = 'på grund av biverkan.';
        break;
      case 'no_effect':
        reason = 'på grund av bristande effekt.';
        break;
      case 'other':
        reason = `på grund av ${data.stoppedMedicationOtherReason?.trim() || '[saknas]'}`;
        break;
      default:
        break;
    }
    return `Har slutat med ${name} (slutade ${date}) ${reason}`;
  }
  return null;
}

export function formatNewMedicationSection(
  data: FollowupFormData
): string | null {
  if (data.hasStartedNewMedication === undefined) return null;
  return data.hasStartedNewMedication
    ? `Har påbörjat behandling med: ${data.newMedicationName?.trim() || '[saknas]'}. Startdatum: ${data.newMedicationStartDate?.trim() || '[saknas]'}. Skäl: ${data.newMedicationReason?.trim() || '[saknas]'}.`
    : 'Inga medicinändringar sedan sist.';
}

export function formatNewConditionSection(
  data: FollowupFormData
): string | null {
  if (data.hasNewCondition === undefined) return null;
  return data.hasNewCondition
    ? `Har sedan sist drabbats av: ${data.newConditionDescription?.trim() || '[saknas]'}`
    : 'Har inte drabbats av någon ny sjukdom sedan sist.';
}

export function formatOtherImportantInfoSection(
  data: FollowupFormData
): string | null {
  return data.otherImportantInfo?.trim()
    ? `Patienten tillägger: ${data.otherImportantInfo.trim()}`
    : null;
}

export function formatBloodPressureSection(
  data: FollowupFormData
): string | null {
  if (data.bloodPressureInstruction !== 'yes_measured') return null;
  return `Patientens blodtryck uppmätt till: ${data.systolicPressure || '?'} / ${data.diastolicPressure || '?'}. Puls: ${data.pulse || '?'}.`;
}

export function formatPrescriptionRenewalSection(
  data: FollowupFormData
): string | null {
  if (!data.needsPrescriptionRenewal) return null;
  return `Patienten önskar förnya recept på: ${data.prescriptionDetails?.trim() || '[saknas]'}`;
}

export function formatMidasSection(formData: FollowupFormData): string {
  const values = [
    formData.midasWorkDays,
    formData.midasReducedWorkDays,
    formData.midasHouseholdMissed,
    formData.midasHouseholdReduced,
    formData.midasSocialMissed,
  ];

  if (values.every((v) => v == null)) return '';
  const score = values.reduce((sum, v) => sum! + (v ?? 0), 0) ?? 0;

  let classification: string;
  if (score <= 5)
    classification = 'Nivå 1 (≤5): låg eller inget funktionshinder.';
  else if (score <= 10) classification = 'Nivå 2 (6–10): milt funktionshinder.';
  else if (score <= 20)
    classification = 'Nivå 3 (11–20): måttligt funktionshinder.';
  else classification = 'Nivå 4 (>21): svårt funktionshinder.';

  return `MIDAS: ${score} poäng - ${classification}`;
}

export function formatEstimatedHeadacheDays(data: FollowupFormData): string {
  if (!data.estimatedHeadacheDaysLast3Months?.trim()) return '';
  return `Patienten uppger att ha haft huvudvärk ${data.estimatedHeadacheDaysLast3Months} dagar de senaste 3 månaderna.`;
}

export function formatMidasPainSection(formData: FollowupFormData): string {
  if (formData.midasPainSeverity == null) return '';
  return `VAS-skalvärde är: ${formData.midasPainSeverity}`;
}

export function formatHITSection(formData: FollowupFormData): string | null {
  const scores = [
    formData.hitPainIntensity,
    formData.hitActivityLimitation,
    formData.hitDesireToLieDown,
    formData.hitFatigueFromHeadache,
    formData.hitIrritabilityFromHeadache,
    formData.hitConcentrationLimitation,
  ];

  if (scores.every((val) => val === undefined)) return null;
  const totalScore = scores.reduce((sum, val) => sum! + (val ?? 0), 0) ?? 0;

  let interpretation = '';
  if (totalScore <= 35)
    interpretation =
      'Patientens svar talar för en lindrig huvudvärksproblematik.';
  else if (totalScore <= 48)
    interpretation =
      'Patientens svar tyder på att huvudvärken är relativt lindrig och inte nämnvärt påverkar hens förmåga till att utföra arbete, studier eller vardagssysslor.';
  else
    interpretation =
      'Patientens svar talar för att hen har en besvärlig huvudvärk som begränsar patienten i vardagen och hindrar hen från viktiga saker som arbete, studier, hushållsarbete och socialt umgänge.';

  return `${totalScore} poäng - ${interpretation}`;
}
